// Components
import NewNavbar from "../../../components/NewNavbar";
import CustomHorizontalBarChart from "../../../components/customCharts/customHorizontalBarChart";
import CustomCircularProgressBar from "../../../components/customCharts/customCircularProgressBar";
import World from "../../../components/maps/World";
import AnnualPerformanceChart from "../../../components/AnnualPerformanceChart";
import LoadingScreen from "../../../components/LoadingScreen";
// Icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faFile, faXmark } from "@fortawesome/free-solid-svg-icons";
// tools
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    LabelList,
    ResponsiveContainer
} from 'recharts';
// custom tools
import { roundNumber } from "../../../utils/roundNumber";
import { extractDate } from "utils/extractDate";
// Constant Data
import { COUNTRYS, WATOMS_MODERN_COLORS_TW } from "../../../constants/constants";
// images
import wabysLogo from '../../../assets/wabys.png';
import ebdaeduLogo from '../../../assets/ebad-edu.png';
import molLogo from '../../../assets/Gov.png';
import person from '../../../assets/person.jpg';
import passport from '../../../assets/passport.jpg';
// APIs
import { fetchAllCandidatesData, fetchDashboardData } from "../../../services/watoms/professionalExamination";
import { fetchCurriculums, fetchSchools } from "../../../services/data";

const WatomsPEDashboard = () => {
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState({});
    const [orgStatus, setOrgStatus] = useState("All");
    const [individualsReportsStatus, setIndividualsReportsStatus] = useState(false);
    const [individualsReportsDate, setIndividualsReportsDate] = useState(null);
    const [candidatesData, setCandidatesData] = useState([]);
    const [courses, setCourses] = useState([]);
    const [noOfCandidatesPerCourse, setNoOfCandidatesPerCourse] = useState([]);
    const [noOfCandidatesPerCountry, setNoOfCandidatesPerCountry] = useState([]);
    const [loading, setLoading] = useState(true);
    const [vtcs, setVtcs] = useState([]);
    const [candidatesScores, setCandidatesScores] = useState([]);
    const [yearChartScore, setYearChartScore] = useState([]);
    const [monthChartScore, setMonthChartScore] = useState([]);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        const loadDatabaseData = async () => {
            const response = await fetchDashboardData();
            setDashboard(response);
            setLoading(false);
        }

        const loadCurriculums = async () => {
            const response = await fetchCurriculums();
            const filteredCurriculums = response.filter(course => course.id !== 1 && course.id !== 2 && course.id !== 3 && course.id !== 13 && course.id !== 14 && course.id !== 15 && course.id !== 16 && course.id !== 17 && course.id !== 18 && course.id !== 48 && course.id !== 49 && course.id !== 50 && course.id !== 51 && course.id !== 53);
            setCourses(filteredCurriculums);
        }

        const loadVtcs = async () => {
            const response = await fetchSchools();
            const filteredOrgs = response.filter(org => org.id !== 12 && org.id !== 14 && org.id !== 1 && org.id !== 2);
            setVtcs(filteredOrgs)
        }

        const loadCandidatesScores = async () => {
            const response = await fetchAllCandidatesData();
            setCandidatesScores(response)
        }

        loadDatabaseData();
        loadCurriculums();
        loadVtcs();
        loadCandidatesScores();
    }, []);

    const getYearlyAveragePerformance = (
        data = [],
        { dateKey = 'fc_start_date', scoreKey = 'fc_test_score' } = {}
    ) => {
        return Object.values(
            data.reduce((acc, item) => {
                const date = item?.[dateKey];
                const score = item?.[scoreKey];

                if (!date || score === null || score === undefined) return acc;

                const year = new Date(date).getFullYear();

                if (!acc[year]) {
                    acc[year] = { year, total: 0, count: 0 };
                }

                acc[year].total += score;
                acc[year].count += 1;

                return acc;
            }, {})
        ).map(({ year, total, count }) => ({
            year,
            performance: total / count,
        }));
    };

    const getMonthlyAveragePerformanceForYear = (
        data = [],
        {
            year = 2026,
            dateKey = 'fc_start_date',
            scoreKey = 'fc_test_score',
        } = {}
    ) => {
        const map = {};

        for (const item of data) {
            const date = item?.[dateKey];
            const score = item?.[scoreKey];

            if (!date || score === null || score === undefined) continue;

            const d = new Date(date);
            const y = d.getFullYear();
            if (y !== year) continue;

            const month = d.getMonth() + 1; // 1..12

            if (!map[month]) map[month] = { month, total: 0, count: 0 };

            map[month].total += score;
            map[month].count += 1;
        }

        return Object.values(map).map(({ month, total, count }) => ({
            month,
            performance: total / count,
        }));
    };

    // yearly scores
    useEffect(() => {
        let candidates;
        if (orgStatus === "All") {
            candidates = dashboard?.candidates || [];
        } else {
            candidates = dashboard?.candidates.filter(candidate => candidate.recommended_country === orgStatus) || [];
        }

        const fcYearlyPerformance = getYearlyAveragePerformance(candidates, {
            dateKey: 'fc_start_date',
            scoreKey: 'fc_test_score',
        });

        const practicalYearlyPerformance = getYearlyAveragePerformance(candidates, {
            dateKey: 'practical_start_date',
            scoreKey: 'practical_test_score',
        });

        const theoryYearlyPerformance = getYearlyAveragePerformance(candidates, {
            dateKey: 'theory_start_date',
            scoreKey: 'theory_test_score',
        });

        const yearlyScoresMap = {};

        [fcYearlyPerformance, practicalYearlyPerformance, theoryYearlyPerformance]
            .forEach(list => {
                list.forEach(({ year, performance }) => {
                    if (!yearlyScoresMap[year]) {
                        yearlyScoresMap[year] = { year, total: 0, count: 0 };
                    }

                    yearlyScoresMap[year].total += performance;
                    yearlyScoresMap[year].count += 1;
                });
            });

        const yearlyScores = Object.values(yearlyScoresMap).map(
            ({ year, total, count }) => ({
                year,
                performance: total / count,
            })
        );

        setYearChartScore(yearlyScores);
    }, [dashboard, orgStatus]);

    // monthly scores
    useEffect(() => {
        const candidates =
            orgStatus === "All"
                ? (dashboard?.candidates || [])
                : (dashboard?.candidates?.filter(c => c.recommended_country === orgStatus) || []);

        const fcMonthly = getMonthlyAveragePerformanceForYear(candidates, {
            year: 2026,
            dateKey: 'fc_start_date',
            scoreKey: 'fc_test_score',
        });

        const practicalMonthly = getMonthlyAveragePerformanceForYear(candidates, {
            year: 2026,
            dateKey: 'practical_start_date',
            scoreKey: 'practical_test_score',
        });

        const theoryMonthly = getMonthlyAveragePerformanceForYear(candidates, {
            year: 2026,
            dateKey: 'theory_start_date',
            scoreKey: 'theory_test_score',
        });

        // merge by month then avg across available tests
        const monthlyScoresMap = {};

        [fcMonthly, practicalMonthly, theoryMonthly].forEach(list => {
            list.forEach(({ month, performance }) => {
                if (!monthlyScoresMap[month]) {
                    monthlyScoresMap[month] = { month, total: 0, count: 0 };
                }
                monthlyScoresMap[month].total += performance;
                monthlyScoresMap[month].count += 1;
            });
        });

        const monthlyScores = Object.values(monthlyScoresMap)
            .sort((a, b) => a.month - b.month)
            .map(({ month, total, count }) => ({
                month,
                performance: total / count,
            }));

        setMonthChartScore(monthlyScores);
    }, [dashboard, orgStatus]);

    useEffect(() => {
        const calculateCandidatesCourses = () => {
            if (!dashboard?.candidates || !Array.isArray(courses)) return;

            if (orgStatus === "All") {
                const grouped = courses
                    .map(course => {
                        const count = dashboard.candidates.filter(
                            candidate => Number(candidate.category) === course.id
                        ).length;

                        return {
                            id: course.id,
                            name: course.code,
                            performance: count
                        };
                    }).sort((a, b) => b.performance - a.performance);
                setNoOfCandidatesPerCourse(grouped);
            } else {
                const filteredCandidates = dashboard.candidates.filter(c => c.recommended_country === orgStatus);
                const grouped = courses
                    .map(course => {
                        const count = filteredCandidates.filter(
                            candidate => Number(candidate.category) === course.id
                        ).length;

                        return {
                            id: course.id,
                            name: course.code,
                            performance: count
                        };
                    }).sort((a, b) => b.performance - a.performance);
                setNoOfCandidatesPerCourse(grouped);
            }
        };

        calculateCandidatesCourses();
    }, [dashboard, courses, orgStatus]);

    useEffect(() => {
        const calculateCandidatesCountries = () => {
            if (!dashboard?.candidates) return;

            if (orgStatus === "All") {
                const grouped = COUNTRYS
                    .map(country => {
                        const count = dashboard.candidates.filter(
                            candidate => candidate.recommended_country === country
                        ).length;

                        return {
                            name: country,
                            performance: count
                        };
                    }).sort((a, b) => b.performance - a.performance);
                setNoOfCandidatesPerCountry(grouped);
            } else {
                const grouped = COUNTRYS
                    .map(country => {
                        const count = dashboard.candidates.filter(
                            candidate => candidate.recommended_country === country
                        ).length;

                        return {
                            name: country,
                            performance: count
                        };
                    })
                    .sort((a, b) => {
                        if (a.name === orgStatus) return -1;
                        if (b.name === orgStatus) return 1;
                        return b.performance - a.performance;
                    });
                setNoOfCandidatesPerCountry(grouped);
            }
        };

        calculateCandidatesCountries();
    }, [dashboard, orgStatus])

    useEffect(() => {
        const filterCandidates = () => {
            if (!dashboard?.candidates) return;

            if (individualsReportsDate === "All") {
                setCandidatesData(dashboard.candidates);
                return;
            }

            if (individualsReportsDate) {
                const filtered = dashboard.candidates.filter(candidate => {
                    if (!candidate.theory_start_date) return false;

                    // Normalize candidate date to YYYY-MM-DD
                    const candidateDate = new Date(candidate.theory_start_date)
                        .toISOString()
                        .split("T")[0];

                    return candidateDate === individualsReportsDate;
                });

                setCandidatesData(filtered);
            } else {
                setIndividualsReportsStatus(false)
            }
        };

        filterCandidates();
    }, [dashboard, individualsReportsDate]);

    const data4 = [
        { month: "يناير", performance: 800 },
        { month: "فبراير", performance: 907 },
        { month: "مارس", performance: 1300 },
        { month: "ابريل", performance: 500 },
        { month: "مايو", performance: 500 },
        { month: "يونيو", performance: 1000 },
        { month: "يوليو", performance: 700 },
        { month: "اغسطس", performance: 300 },
        { month: "سبتمبر", performance: 0 },
        { month: "اكتوبر", performance: 1500 },
        { month: "نوفمبر", performance: 0 },
        { month: "ديسمبر", performance: 0 },
    ];

    const handleNext = () => {
        if (orgStatus === "All") {
            setOrgStatus(COUNTRYS[0]);
        } else {
            const index = COUNTRYS.indexOf(orgStatus);
            if (index < COUNTRYS.length - 1) {
                setOrgStatus(COUNTRYS[index + 1]);
            } else {
                setOrgStatus("All");
            }
        }
    };

    const handlePrevious = () => {
        // if currently All → go to last country
        if (orgStatus === "All") {
            setOrgStatus(COUNTRYS[COUNTRYS.length - 1]);
        } else {
            const index = COUNTRYS.indexOf(orgStatus);
            // if not first → move back; if first → go to "All"
            if (index > 0) {
                setOrgStatus(COUNTRYS[index - 1]);
            } else {
                setOrgStatus("All");
            }
        }
    };

    const IndividualsReportSheet = () => {
        return (
            <div className="fixed inset-0 bg-black/60 flex flex-col overflow-y-auto justify-start gap-6 items-center z-50">
                <button
                    onClick={() => setIndividualsReportsStatus(false)}
                    className="absolute top-4 right-4 text-white bg-gray-700 hover:bg-gray-800 w-12 h-12 flex justify-center items-center text-2xl font-bold cursor-pointer z-50"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
                {candidatesData.map(candidate => {
                    const oceanObj =
                        candidatesScores
                            ?.find(s => s.candidate_id === candidate?.id)
                            ?.scores?.["(OCEAN)"];
                    const catObj =
                        candidatesScores
                            ?.find(s => s.candidate_id === candidate?.id)
                            ?.scores?.["(CAT)"];
                    const sjtObj =
                        candidatesScores
                            ?.find(s => s.candidate_id === candidate?.id)
                            ?.scores?.["(SJT)"];

                    const oceanValues = oceanObj ? Object.values(oceanObj).map(Number) : [];
                    const oceanAverage =
                        oceanValues.length > 0
                            ? oceanValues.reduce((sum, v) => sum + v, 0) / oceanValues.length
                            : 0;
                    const candidateScores = [
                        { id: 1, name: "OCEAN", performance: oceanAverage },
                        { id: 2, name: "CAT", performance: catObj },
                        { id: 3, name: "SJT", performance: sjtObj },
                    ]
                    return (
                        <div className="relative w-full flex flex-col items-center">
                            {/* Page 1 */}
                            <div className="max-w-5xl w-[40%] min-w-[700px] h-fit flex text-black bg-white p-3 mt-4">
                                <div className="w-full flex flex-col border-black border-2 rounded-xl p-2">
                                    {/* Header */}
                                    <div className="flex justify-between items-center w-full">
                                        {/* logo */}
                                        <div className="flex flex-col items-center w-14">
                                            <img src={wabysLogo} className="w-14" alt="ebda edu logo" />
                                            <img src={ebdaeduLogo} className="w-10" alt="ebda edu logo" />
                                        </div>
                                        {/* title */}
                                        <div className="text-black flex justify-center gap-2 text-xs font-bold border-b-2 border-black">
                                            {individualsReportsDate && individualsReportsDate !== "All" && <h1 className="">بتاريخ {extractDate(individualsReportsDate)}</h1>}
                                            <h1 className="">التقرير النهائي للفحص المهني الشامل</h1>
                                        </div>
                                        {/* logo */}
                                        <div className="flex flex-col">
                                            <img src={molLogo} className="w-14" alt="ebda edu logo" />
                                        </div>
                                    </div>
                                    <div className="rounded-xl shadow-black shadow-md p-2 flex flex-col mt-2 w-full gap-2">
                                        {/* user's data */}
                                        <h1 className="text-xs text-end font-bold">:اولاً: البيانات الاساسية للمتقدم</h1>
                                        <div className="w-full border-black border-2 flex justify-end items-center p-1 gap-1 min-h-20">
                                            <div className="flex flex-col gap-1 w-[20%] text-[7px] font-bold justify-center items-center">
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">{candidate?.email}</div>
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">{candidate?.phone_number}</div>
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">{candidate?.recommended_country}</div>
                                            </div>
                                            <div className="flex flex-col gap-1 min-w-fit w-[10%] text-[7px] font-bold justify-center items-center">
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">البريد الالكتروني</div>
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">رقم الموبيل</div>
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">الدولة</div>
                                            </div>
                                            <div className="flex flex-col gap-1 w-[20%] text-[7px] font-bold justify-center items-center">
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">{vtcs.find(obj => obj.id === Number(candidate?.organization_id)).name}</div>
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">{courses.find(obj => obj.id === Number(candidate?.category)).code}</div>
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">{candidate?.candidate_id}</div>
                                            </div>
                                            <div className="flex flex-col gap-1 min-w-fit w-[10%] text-[7px] font-bold justify-center items-center">
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">مركز الاختبار</div>
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">الفئة</div>
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">رقم الممتحن</div>
                                            </div>
                                            <div className="flex flex-col gap-1 w-[30%] text-[7px] font-bold justify-center items-center">
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">{candidate?.name}</div>
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">{candidate?.id_number}</div>
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">{candidate?.passport_number}</div>
                                            </div>
                                            <div className="flex flex-col gap-1 min-w-fit w-[10%] text-[7px] font-bold justify-center items-center">
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">المرشح</div>
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">الرقم القومي</div>
                                                <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">رقم جواز السفر</div>
                                            </div>
                                            {/* user's img */}
                                            <div className="flex flex-col gap-1">
                                                <img className="w-20 min-w-20 border-black border-2 p-1 rounded-2xl" src={person} alt="" />
                                            </div>
                                        </div>
                                        <h1 className="text-xs text-end font-bold">:ثانيا: ملخص الاداء والقرار الموصي به</h1>
                                        <div className="rounded-2xl overflow-hidden border border-black">
                                            <table className="w-full table-fixed text-white text-center" dir="rtl">
                                                <thead>
                                                    <tr className="bg-[#5268b1] text-xs">
                                                        <th className="py-2 border border-black font-semibold w-1/3">اجمالي التقييم</th>
                                                        <th className="py-2 border border-black font-semibold w-1/3">القرار و التوصيات</th>
                                                        <th className="py-2 border border-black font-semibold w-1/3">ملاحظات</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    <tr className="bg-white text-black transition">
                                                        <td className="py-3 border border-black">90%</td>
                                                        <td className="py-3 border border-black">اختبار</td>
                                                        <td className="py-3 border border-black">لا توجد ملاحظات</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="flex flex-col bg-gray-200 rounded-2xl p-2">
                                            <h1 className="text-center font-bold mb-2">ترتيب عناصر التقييم</h1>
                                            <div className="flex justify-between items-center gap-2 text-black">
                                                {/* Overall Score Circle */}
                                                <div className="flex flex-col items-center justify-center cursor-pointer">
                                                    <CustomCircularProgressBar value={roundNumber(90)} size={100} color='url(#circularBlueGradient)' bg='#23263a' textColor='#fff' />
                                                    <span className='text-black mt-2'>الاجمالي</span>
                                                </div>
                                                {/* Performance Bars */}
                                                <div className="flex-1 flex flex-col gap-1">
                                                    {candidateScores.map((s, idx) => (
                                                        <div className='flex justify-between items-center mb-1'>
                                                            <span className="text-sm font-bold text-black w-fit px-1" >{roundNumber(s.performance)}%</span>
                                                            <div className="relative min-w-3/6 max-w-3/6 w-3/6 h-[22px] bg-[#444652] rounded-[18px] shadow-[0_2px_8px_#0002] overflow-hidden cursor-pointer transition-shadow duration-200 ease-in-out" >
                                                                {/* Bar fill */}
                                                                <div
                                                                    className={`w-[${roundNumber(s.performance)}%] h-full ${WATOMS_MODERN_COLORS_TW[idx]} rounded-full transition-[width] duration-[700ms] ease-[cubic-bezier(.4,2,.6,1)]`} />
                                                            </div>
                                                            <span className="min-w-2/6 max-w-2/6 w-2/6 font-bold text-black text-center">{s.name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <h1 className="text-xs text-end font-bold">:ثالثا: تحليل الاداء في المحاور الرئيسية</h1>
                                        <table className="w-full table-fixed border-separate border-spacing-0 rounded-xl overflow-hidden" dir="rtl">
                                            <thead className="h-16">
                                                <tr className="bg-[#6b7dcf] text-white text-xs">
                                                    <th className="w-[20%] py-2 text-center font-semibold border-l border-black">الاسم الاختبار</th>
                                                    <th className="w-[10%] py-2 text-center font-semibold border-b border-l border-black">من</th>
                                                    <th className="w-[10%] py-2 text-center font-semibold border-b border-l border-black">الي</th>
                                                    <th className="w-[10%] py-2 text-center font-semibold border-l border-black">التقييم</th>
                                                    <th className="w-[12%] py-2 text-center font-semibold border-l border-black px-1">صورة جانبية</th>
                                                    <th className="w-[12%] py-2 text-center font-semibold border-black">صورة أمامية</th>
                                                    <th className="w-[26%] py-2 text-center font-semibold border-r border-black">وضع الاداء مقارنة بالمعايير</th>
                                                </tr>
                                            </thead>

                                            <tbody className="divide-y divide-slate-100">
                                                {[
                                                    { name: "السمات الشخصية و السلوكية (OCEAN)", score: candidateScores.find(d => d.name === "OCEAN").performance },
                                                    { name: "القدرات المعرفية الاساسية (CAT)", score: candidateScores.find(d => d.name === "CAT").performance },
                                                    { name: "المهارات الوظيفية الناعمة (SJT)", score: candidateScores.find(d => d.name === "SJT").performance },
                                                    { name: "المعارف الفنية المتخصص (JCT)", score: 0 },
                                                    { name: "المهارات الفنية المتخصصة (PST)", score: 0 },
                                                    { name: "ملفات تاكيد الهوية", score: 0 }
                                                ].map((exam, index) => (
                                                    <tr key={index} className="bg-white transition-colors">
                                                        <td className="h-20 max-h-20 py-2 text-center border-r border-b border-black">
                                                            <span className="text-xs inline-flex items-center justify-center min-w-[2.25rem] px-2 h-6 min-h-fit rounded-full text-black">
                                                                {exam.name}
                                                            </span>
                                                        </td>

                                                        <td className="h-20 py-2 text-center border-r border-b border-black whitespace-nowrap">
                                                            <span className="text-xs inline-flex items-center justify-center px-2 h-6 min-h-fit rounded-full text-black">
                                                                14:16
                                                            </span>
                                                        </td>

                                                        <td className="h-20 py-2 text-center border-r border-b border-black whitespace-nowrap">
                                                            <span className="text-xs inline-flex items-center justify-center px-2 h-6 min-h-fit rounded-full text-black">
                                                                15:16
                                                            </span>
                                                        </td>

                                                        {/* التقييم */}
                                                        <td className="h-20 max-h-20 py-2 w-[25%] border-r border-b border-black">
                                                            <div className="w-[95%] h-5 flex justify-center items-center gap-2 rounded-full">
                                                                <span className="inline-flex items-center justify-center px-2 h-6 text-black">{roundNumber(exam.score) || 0}%</span>
                                                            </div>
                                                        </td>

                                                        {/* صور */}
                                                        <td className="h-20 max-h-20 p-2 text-center border-x border-b border-black">
                                                            <div className="flex justify-center items-center gap-2 w-full h-14">
                                                                <div className="flex-1 h-full bg-gray-400" />
                                                            </div>
                                                        </td>

                                                        <td className="h-20 max-h-20 p-2 text-center border-l border-b border-black">
                                                            <div className="flex justify-center items-center gap-2 w-full h-14">
                                                                <div className="flex-1 h-full bg-gray-400" />
                                                            </div>
                                                        </td>

                                                        {/* الملاحظات */}
                                                        <td className="h-20 max-h-20 py-2 text-center border-l border-b border-black w-[18%] break-words">
                                                            <span className="inline-flex items-center justify-center min-w-[2.25rem] px-2 h-6 rounded-full text-black">
                                                                لا يوجد
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                            {/* page 2 */}
                            <div className="ax-w-5xl w-[40%] h-fit flex text-black bg-white p-3 mt-4">
                                <div className="w-full flex flex-col border-black border-2 rounded-xl p-2 gap-2">
                                    <h1 className="text-xs text-end font-bold">:رابعا : نموذج التقييم العملي</h1>
                                    <div className="w-full border-black border-2 flex justify-end items-center p-1 gap-1 min-h-20">
                                        <div className="flex flex-col gap-1 w-[20%] text-[7px] font-bold justify-center items-center">
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">test@test.com</div>
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">0110659889</div>
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">السعودية</div>
                                        </div>
                                        <div className="flex flex-col gap-1 min-w-fit w-[10%] text-[7px] font-bold justify-center items-center">
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">البريد الالكتروني</div>
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">رقم الموبيل</div>
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">الدولة</div>
                                        </div>
                                        <div className="flex flex-col gap-1 w-[20%] text-[7px] font-bold justify-center items-center">
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">مركز الشرابية</div>
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">تجارة (1)</div>
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">74211</div>
                                        </div>
                                        <div className="flex flex-col gap-1 min-w-fit w-[10%] text-[7px] font-bold justify-center items-center">
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">مركز الاختبار</div>
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">الفئة</div>
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">رقم الممتحن</div>
                                        </div>
                                        <div className="flex flex-col gap-1 w-[30%] text-[7px] font-bold justify-center items-center">
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">محمد احمد سيد محمد</div>
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">35286468437246</div>
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center">284-871971871</div>
                                        </div>
                                        <div className="flex flex-col gap-1 min-w-fit w-[10%] text-[7px] font-bold justify-center items-center">
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">المرشح</div>
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">الرقم القومي</div>
                                            <div className="border-black border-2 h-fit w-full text-center px-2 py-1 flex justify-center items-center bg-gray-300">رقم جواز السفر</div>
                                        </div>
                                        {/* user's img */}
                                        <div className="flex flex-col gap-1">
                                            <img className="w-20 min-w-20 border-black border-2 p-1 rounded-2xl" src={person} alt="" />
                                        </div>
                                    </div>
                                    <table className="w-full border border-black text-center text-[7px]" dir="rtl">
                                        <thead>
                                            <tr>
                                                <th colSpan={8} className="border border-black py-1">
                                                    غير مرضى = 0 &nbsp; ضعيف = 1 &nbsp; أقل من المتوسط = 2 &nbsp; مرضي = 3 &nbsp; جيد = 4 &nbsp; ممتاز = 5
                                                </th>
                                            </tr>
                                            <tr className="bg-[#007d88] text-white">
                                                <th className="border border-black w-[5%] py-1">#</th>
                                                <th className="border border-black w-[55%] py-1">البيان</th>
                                                <th colSpan={6} className="border border-black py-1">التقييمات</th>
                                            </tr>
                                            <tr className="bg-[#008b95] text-white">
                                                <th colSpan={2}></th>
                                                <th className="border border-black w-[7%] py-1">0</th>
                                                <th className="border border-black w-[7%] py-1">1</th>
                                                <th className="border border-black w-[7%] py-1">2</th>
                                                <th className="border border-black w-[7%] py-1">3</th>
                                                <th className="border border-black w-[7%] py-1">4</th>
                                                <th className="border border-black w-[7%] py-1">5</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {/* Section 1 */}
                                            <tr className="bg-gray-100">
                                                <td colSpan={8} className="border border-black text-right font-bold px-2">
                                                    فهم المشكلة وسير العملية التجريبية
                                                </td>
                                            </tr>
                                            {[
                                                "هل أظهر المرشح فهما لأهداف المهمة والغرض منها والعمل على تحقيقها؟",
                                                "استخدم المرشح المواد الخام بطريقة لجمع المعلومات الضرورية أو حل المسائل؟",
                                                "هل أظهر المرشح جميع التعليمات؟",
                                                "هل أظهر المرشح مهارات فعالة في إدارة الوقت و إكمال المهمة ضمن إطار زمني معقول؟",
                                                "هل أظهر المرشح القدرة على اتباع التسلسل المنطقي وترتيب المهام بالترتيب الصحيح؟",
                                            ].map((text, i) => (
                                                <tr key={i}>
                                                    <td className="border border-black py-2 font-bold">Q{i + 1}</td>
                                                    <td className="border border-black text-right px-2">{text}</td>
                                                    {[0, 1, 2, 3, 4, 5].map((n) => (
                                                        <td key={n} className="border border-black"></td>
                                                    ))}
                                                </tr>
                                            ))}

                                            {/* Section 2 */}
                                            <tr className="bg-gray-100">
                                                <td colSpan={8} className="border border-black text-right font-bold px-2">
                                                    الأمن والسلامة
                                                </td>
                                            </tr>
                                            {[
                                                "هل اختار المرشح معدات الحماية الشخصية المناسبة (PPE) وارتداها قبل تنفيذ المهمة؟",
                                                "هل اختار المرشح جميع احتياطات السلامة أثناء أداء المهمة؟",
                                                "هل تخلص المرشح من المواد أو النفايات الخطرة بطريقة صحيحة تراعي سلامة الآخرين؟",
                                            ].map((text, i) => (
                                                <tr key={`safety-${i}`}>
                                                    <td className="border border-black py-2 font-bold">Q{i + 1}</td>
                                                    <td className="border border-black text-right px-2">{text}</td>
                                                    {[0, 1, 2, 3, 4, 5].map((n) => (
                                                        <td key={n} className="border border-black"></td>
                                                    ))}
                                                </tr>
                                            ))}

                                            {/* Section 3 */}
                                            <tr className="bg-gray-100">
                                                <td colSpan={8} className="border border-black text-right font-bold px-2">
                                                    الأدوات والمعـدات
                                                </td>
                                            </tr>
                                            {[
                                                "هل أظهر المرشح الكفاءة في استخدام الأدوات والمعدات المطلوبة للمهمة؟",
                                                "هل استخدم المرشح المعدات الأساسية و الأدوات لتحديد المشكلة التجريبية؟",
                                                "هل أظهر المرشح مهارة تركيب/إصلاح/استبدال المكونات أثناء العرض؟",
                                            ].map((text, i) => (
                                                <tr key={`tools-${i}`}>
                                                    <td className="border border-black py-2 font-bold">Q{i + 1}</td>
                                                    <td className="border border-black text-right px-2">{text}</td>
                                                    {[0, 1, 2, 3, 4, 5].map((n) => (
                                                        <td key={n} className="border border-black"></td>
                                                    ))}
                                                </tr>
                                            ))}

                                            {/* Section 4 */}
                                            <tr className="bg-gray-100">
                                                <td colSpan={8} className="border border-black text-right font-bold px-2">
                                                    الصحة والنظافة
                                                </td>
                                            </tr>
                                            {[
                                                "هل حافظ المرشح على منطقة عمل نظيفة ومنظمة طوال فترة المهمة؟",
                                                "هل قام المرشح بتسليم جميع المعدات في حالة جيدة؟",
                                            ].map((text, i) => (
                                                <tr key={`hygiene-${i}`}>
                                                    <td className="border border-black py-2 font-bold">Q{i + 1}</td>
                                                    <td className="border border-black text-right px-2">{text}</td>
                                                    {[0, 1, 2, 3, 4, 5].map((n) => (
                                                        <td key={n} className="border border-black"></td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {/* Bottom summary table */}
                                    <table className="w-full border border-black text-center text-[7px] mt-2" dir="rtl">
                                        <thead>
                                            <tr className="bg-[#008b95] text-white">
                                                <th colSpan={3} className="border border-black py-1">البيان</th>
                                                <th colSpan={11} className="border border-black py-1">الأدنى</th>
                                                <th colSpan={11} className="border border-black py-1">الأعلى</th>
                                            </tr>
                                            <tr className="bg-[#00a3af] text-white">
                                                <th colSpan={3}></th>
                                                {[...Array(11)].map((_, i) => (
                                                    <th key={i} className="border border-black w-[3%] py-1">{i}</th>
                                                ))}
                                                {[...Array(11)].map((_, i) => (
                                                    <th key={`high-${i}`} className="border border-black w-[3%] py-1">
                                                        {10 - i}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[
                                                "قيم المرشح بناءً على أدائه العام في المهمة الموكلة إليه",
                                                "أتم المرشح بنجاح إنجاز النتيجة النهائية",
                                                "قيّم القدرة الكلية للمرشح على استخدام الأدوات والمعدات المخصصة",
                                            ].map((text, i) => (
                                                <tr key={i}>
                                                    <td className="border border-black py-2 font-bold">Q{i + 1}</td>
                                                    <td className="border border-black text-right px-2" colSpan={2}>{text}</td>
                                                    {[...Array(22)].map((_, j) => (
                                                        <td key={j} className="border border-black"></td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div className="flex flex-col gap-2 text-[7px]">
                                        <h1 className="text-center">الاقرار و الالتزام</h1>
                                        <h1 className="text-end px-1">انا الموقع أدناه, أقر بموجب هذا بأنني أتحمل المسؤلية الكاملة عن دقة المعلومات المقدمة في نموذج التقييم العملي هذا. وأقر بأنني سأتحمل المسؤلية القانونية اذا تبين ايا من المعلومات المذكورة أعلاه غير صحيحة او مضللة</h1>
                                        <h1 className="text-end px-1">:معلومات المقييم</h1>
                                        <div className="w-full flex justify-between text-[7px]">
                                            <div className="h-8 flex justify-center gap-1">
                                                <label className="text-[7px]">التوقيع الالكتروني</label>
                                            </div>
                                            <div className="h-8 flex justify-center gap-1">
                                                <label className="text-[7px]">:رقم الهوية</label>
                                            </div>
                                            <div className="h-8 flex justify-center gap-1">
                                                <label className="text-[7px]">:اسم المراقب</label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* page 3 */}
                            <div className="max-w-5xl w-[40%] h-fit flex text-black bg-white p-3 mt-4">
                                <div className="w-full flex flex-col items-center border-black border-2 rounded-xl p-2 gap-2">
                                    <h1 className="w-full text-xs text-end font-bold">رابعا - جواز السفر</h1>
                                    {/* passport data */}
                                    <img src={passport} alt="" />
                                    <h1 className="w-full text-xs text-end font-bold">خامسا - تذكرة امتحان المتقدم</h1>
                                    <div className="border-black border-2 rounded-xl w-[98%] h-[500px] bg-gray-500" alt="" />
                                    <h1 className="text-sm text-center font-bold">مع خالص الشكر و التقدير</h1>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    const IndividualsReportDateSelector = () => {
        let selectedDate = null;
        let selectedAll = false;
        const handleDateChange = (e) => {
            selectedDate = e.target.value;
            selectedAll = false;
            document.getElementById("allDays").checked = false; // reset checkbox
        };

        const handleCheckChange = (e) => {
            selectedAll = e.target.checked;
            selectedDate = null;
            document.getElementById("dateInput").value = ""; // reset date input
        };

        const handleSubmit = () => {
            if (selectedAll) {
                setIndividualsReportsDate("All");
            } else if (selectedDate) {
                setIndividualsReportsDate(selectedDate);
            } else {
                setIndividualsReportsDate(null);
            }
        };
        return (
            <div className="fixed inset-0 bg-black/60 flex overflow-y-auto justify-center gap-6 items-center z-50">
                <button
                    onClick={() => setIndividualsReportsStatus(false)}
                    className="absolute top-4 right-4 text-white bg-gray-700 hover:bg-gray-800 w-12 h-12 flex justify-center items-center text-2xl font-bold cursor-pointer z-50"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
                <div
                    className="relative w-full max-w-md bg-white text-black p-4 rounded-xl shadow-md"
                    dir="rtl"
                >
                    <div className="w-full flex flex-col gap-4 border border-gray-300 rounded-xl p-4">

                        <h1 className="text-xl font-bold text-right">اختيار التاريخ</h1>

                        {/* Date selection */}
                        <div className="flex items-center justify-between gap-3">
                            <h2 className="text-lg font-semibold">اختيار يوم محدد:</h2>
                            <input
                                id="dateInput"
                                type="date"
                                className="border px-2 py-1 rounded-lg"
                                onChange={handleDateChange}
                            />
                        </div>

                        {/* All days */}
                        <div className="flex items-center justify-between gap-3">
                            <label className="text-base font-medium">اختيار كل الأيام</label>
                            <input
                                id="allDays"
                                type="checkbox"
                                className="w-5 h-5 accent-blue-600 cursor-pointer"
                                onChange={handleCheckChange}
                            />
                        </div>

                        <button
                            onClick={handleSubmit}
                            className="mt-2 py-2 px-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition"
                        >
                            اختيار
                        </button>

                        {individualsReportsDate && (
                            <p className="text-right text-green-600 font-semibold">
                                القيمة المختارة: {individualsReportsDate}
                            </p>
                        )}

                    </div>
                </div>
            </div>
        )
    }

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-gray-800 text-white p-2 rounded text-sm">
                    <p>{`Year: ${label}`}</p>
                    <p>{`Performance: ${payload[0].value}`}</p>
                </div>
            );
        }
        return null;
    };

    if (loading) return <LoadingScreen />;
    const avgScore =
        orgStatus === "All"
            ? dashboard?.total
            : dashboard?.resultsByCountry?.[orgStatus];

    const avgPerformance =
        avgScore?.length
            ? avgScore.reduce((sum, item) => sum + (item.performance ?? 0), 0) / avgScore.length
            : 0;


    return (
        <div className="w-full min-h-screen bg-gradient-to-br from-[#0a183d] via-[#0d1f4a] to-[#0a183d]">
            <NewNavbar
                shareStatus={false}
                darkmodeStatus={false}
                searchStatus={false}
            >
                <div className="flex gap-3">
                    {/* Mobile Menu Toggle */}
                    <button
                        className="lg:hidden rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        title="Menu"
                    >
                        <svg className="w-6 h-6 text-watomsBlue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>

                    <button
                        className="rounded-full w-10 h-10 flex justify-center items-center bg-white/80 hover:bg-gray-200 shadow transition-all"
                        title="Reports"
                        onClick={() => setIndividualsReportsStatus(true)}
                    >
                        <FontAwesomeIcon
                            icon={faFile}
                            className="text-xl text-watomsBlue"
                        />
                    </button>
                </div>
            </NewNavbar>

            <div className="w-full min-h-[calc(100vh-60px)] flex relative">
                {/* Sidebar - Mobile Overlay */}
                {sidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Sidebar */}
                <div className={`
                    fixed lg:sticky top-0 left-0 h-screen
                    w-64 lg:w-48 xl:w-64
                    bg-white/90 backdrop-blur-md
                    flex flex-col justify-start gap-4 p-4
                    transform transition-transform duration-300 ease-in-out
                    z-50 lg:z-auto
                    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                    shadow-2xl lg:shadow-none
                `}>
                    {/* Close button for mobile */}
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden absolute top-4 right-4 text-gray-600 hover:text-gray-900"
                    >
                        <FontAwesomeIcon icon={faXmark} className="text-2xl" />
                    </button>

                    <div className="text-center font-bold text-gray-800 text-lg pt-8 lg:pt-0">إجراءات الحوكمة</div>

                    <button
                        onClick={() => { navigate('/watoms/pe'); setSidebarOpen(false); }}
                        className="w-full min-h-[44px] flex justify-center items-center text-yellow-400 text-sm text-center bg-[#0a183d] hover:bg-gray-700 px-3 py-2 rounded-xl border-blue-600 border-2 transition-all"
                    >
                        ملفات تاكيد الهوية
                    </button>
                    <button
                        onClick={() => { navigate('/watoms/pe'); setSidebarOpen(false); }}
                        className="w-full min-h-[44px] flex justify-center items-center text-yellow-400 text-sm text-center bg-[#0a183d] hover:bg-gray-700 px-3 py-2 rounded-xl border-blue-600 border-2 transition-all"
                    >
                        تقييم مسئول الجودة والحوكمة
                    </button>
                    <button
                        onClick={() => { navigate('/watoms/pe/observer-evaluation'); setSidebarOpen(false); }}
                        className="w-full min-h-[44px] flex justify-center items-center text-yellow-400 text-sm text-center bg-[#0a183d] hover:bg-gray-700 px-3 py-2 rounded-xl border-blue-600 border-2 transition-all"
                    >
                        تقييم مراقب الاختبار
                    </button>
                    <button
                        onClick={() => { navigate('/watoms/pe'); setSidebarOpen(false); }}
                        className="w-full min-h-[44px] flex justify-center items-center text-yellow-400 text-sm text-center bg-[#0a183d] hover:bg-gray-700 px-3 py-2 rounded-xl border-blue-600 border-2 transition-all"
                    >
                        اختبارات المرشحين
                    </button>
                </div>

                {/* Main Content */}
                <div className="flex-1 w-full overflow-x-hidden p-2 md:p-4 lg:p-6">
                    <div className="max-w-[1920px] mx-auto">
                        {/* Country Navigator - Mobile Optimized */}
                        <div className="flex justify-between items-center mb-4 md:mb-6 bg-white/10 backdrop-blur-md rounded-xl p-3 md:p-4">
                            <button
                                className="flex justify-center items-center text-yellow-400 rounded-full hover:bg-white/10 min-w-[44px] min-h-[44px] transition-all"
                                onClick={handlePrevious}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
                            </button>

                            <h1 className="text-sm md:text-lg text-yellow-400 text-center font-bold">
                                مؤشرات الفحص المهني{" "}
                                {orgStatus === "All" ? "الدولي" : orgStatus}
                            </h1>

                            <button
                                className="flex justify-center items-center text-yellow-400 rounded-full hover:bg-white/10 min-w-[44px] min-h-[44px] transition-all"
                                onClick={handleNext}
                            >
                                <FontAwesomeIcon icon={faArrowRight} className="text-xl" />
                            </button>
                        </div>

                        {/* Statistics Cards - Responsive Grid */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
                            <div className="col-span-2 lg:col-span-1 bg-white/10 backdrop-blur-md rounded-xl p-4 shadow-lg">
                                <div className="text-yellow-400 text-center font-bold text-sm md:text-base mb-3">
                                    عدد المراكز المعتمدة
                                </div>
                                <div className="flex justify-around items-center text-white text-xs">
                                    <div className="flex flex-col items-center gap-2">
                                        <span className="font-medium">المفعل</span>
                                        <span className="rounded-full w-12 h-12 md:w-14 md:h-14 flex justify-center items-center text-lg font-black bg-green-500">
                                            38
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-center gap-2">
                                        <span className="font-medium">غير مفعّل</span>
                                        <span className="rounded-full w-12 h-12 md:w-14 md:h-14 flex justify-center items-center text-lg font-black bg-red-500">
                                            00
                                        </span>
                                    </div>
                                    <div className="flex flex-col items-center gap-2">
                                        <span className="font-medium">إجمالي</span>
                                        <span className="rounded-full w-12 h-12 md:w-14 md:h-14 flex justify-center items-center text-lg font-black bg-cyan-400 text-black">
                                            38
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 shadow-lg">
                                <div className="text-yellow-400 text-center font-bold text-xs md:text-sm mb-2">نسبة النجاح</div>
                                <div className="text-white text-2xl md:text-3xl text-center font-bold">0%</div>
                            </div>

                            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 shadow-lg">
                                <div className="text-yellow-400 text-center font-bold text-xs md:text-sm mb-2">الناجحين</div>
                                <div className="text-white text-2xl md:text-3xl text-center font-bold">0</div>
                            </div>

                            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 shadow-lg">
                                <div className="text-yellow-400 text-center font-bold text-xs md:text-sm mb-2">المتقدمين</div>
                                <div className="text-white text-2xl md:text-3xl text-center font-bold">{dashboard?.candidates?.length || 0}</div>
                            </div>
                        </div>

                        {/* Main Dashboard Grid - Responsive */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                            {/* Left Column */}
                            <div className="space-y-4 md:space-y-6">
                                {/* Yearly Chart */}
                                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 shadow-lg">
                                    <h2 className="text-yellow-400 text-center font-bold text-sm md:text-base mb-4">
                                        تحليل معدل تغيير الاداء السنوي
                                    </h2>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <LineChart data={yearChartScore}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#444" opacity={0.3} />
                                            <XAxis
                                                dataKey="year"
                                                stroke="#888"
                                                fontSize={10}
                                                tick={{ fill: '#fff' }}
                                            />
                                            <YAxis
                                                stroke="#888"
                                                fontSize={10}
                                                tick={{ fill: '#fff' }}
                                                domain={[0, 100]}
                                                ticks={[25, 50, 75, 100]}
                                            />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Line
                                                type="monotone"
                                                dataKey="performance"
                                                stroke="#facc15"
                                                strokeWidth={3}
                                                dot={(props) => (
                                                    <circle
                                                        cx={props.cx}
                                                        cy={props.cy}
                                                        r={6}
                                                        fill="#facc15"
                                                        stroke="#fff"
                                                        strokeWidth={2}
                                                    />
                                                )}
                                                activeDot={{
                                                    r: 8,
                                                    fill: '#facc15',
                                                    stroke: '#fff',
                                                    strokeWidth: 2
                                                }}
                                            >
                                                <LabelList
                                                    dataKey="performance"
                                                    position="top"
                                                    offset={10}
                                                    fill="#facc15"
                                                    fontSize={11}
                                                    fontWeight="bold"
                                                    formatter={(value) => `${Math.round(value)}`}
                                                />
                                            </Line>
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Country Distribution */}
                                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 shadow-lg">
                                    <CustomHorizontalBarChart
                                        data={noOfCandidatesPerCountry}
                                        title={"التعداد الدولي للعمالة المصدرة"}
                                    />
                                </div>
                            </div>

                            {/* Middle Column */}
                            <div className="space-y-4 md:space-y-6">
                                {/* World Map */}
                                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 shadow-lg">
                                    <World ids={[1, 2, 3]} />
                                </div>

                                {/* Benchmarks */}
                                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 shadow-lg">
                                    <h3 className="text-yellow-400 text-sm md:text-base font-bold mb-4 text-center">
                                        مؤشرات حوكمة اختبارات التقديم
                                    </h3>
                                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                                        {/* Overall Score */}
                                        <div className="flex flex-col items-center justify-center">
                                            <CustomCircularProgressBar
                                                value={roundNumber(avgPerformance)}
                                                size={100}
                                                color='url(#circularBlueGradient)'
                                                bg='#23263a'
                                                textColor='#fff'
                                            />
                                            <span className='text-white mt-2 text-sm'>الاجمالي</span>
                                        </div>
                                        {/* Performance Bars */}
                                        <div className="flex-1 w-full flex flex-col gap-2">
                                            {(orgStatus === "All" ? dashboard?.total : dashboard?.resultsByCountry?.[orgStatus])?.map((s, idx) => (
                                                <div key={idx} className='flex justify-between items-center'>
                                                    <span className="text-xs md:text-sm font-bold text-white">{roundNumber(s.performance)}%</span>
                                                    <div className="flex-1 mx-2 h-[22px] bg-[#444652] rounded-full overflow-hidden">
                                                        <div
                                                            style={{ width: `${roundNumber(s.performance)}%` }}
                                                            className={`h-full ${WATOMS_MODERN_COLORS_TW[idx % WATOMS_MODERN_COLORS_TW.length]} rounded-full transition-all duration-700`}
                                                        />
                                                    </div>
                                                    <span className="text-[9px] md:text-[10px] font-medium text-white text-right w-20">{s.name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-4 md:space-y-6">
                                {/* Course Distribution */}
                                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 shadow-lg">
                                    <CustomHorizontalBarChart
                                        data={noOfCandidatesPerCourse}
                                        title={"الترتيب العام لتعداد التخصصات"}
                                    />
                                </div>

                                {/* Monthly Chart */}
                                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 shadow-lg">
                                    <AnnualPerformanceChart
                                        data={monthChartScore}
                                        title={`تحليل معدل تغيير الاداء الشهري`}
                                        YAxisDuration={[0, 100]}
                                        YAxisTicks={[0, 25, 50, 75, 100]}
                                        height="h-[300px] md:h-[400px]"
                                        valueType="number"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {individualsReportsStatus && individualsReportsDate && candidatesData.length > 0 && <IndividualsReportSheet />}
                {individualsReportsStatus && !individualsReportsDate && <IndividualsReportDateSelector />}
            </div>
        </div>
    )
}

export default WatomsPEDashboard;